import React from 'react';

export interface GtLegendItem {
    name: string;
    color: string;
    /** null for pie/donut slices */
    total: number | null;
}

interface GtChartLegendProps {
    items: GtLegendItem[];
    pos: 'bottom' | 'top' | 'right';
    isDark: boolean;
    labelColor: string;
    isLineOrArea: boolean;
}

export function GtChartLegend({ items, pos, isDark, labelColor, isLineOrArea }: GtChartLegendProps) {
    if (pos === 'bottom') {
        // ── Compact inline legend strip ───────────────────────────────────────
        const fmtTotal = (v: number) =>
            v >= 1_000_000 ? `${+(v / 1_000_000).toFixed(1)}M`
                : v >= 1_000 ? `${+(v / 1_000).toFixed(1)}k`
                    : String(v);

        const sepColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '2px 0',
                borderTop: `1px solid ${sepColor}`,
                padding: '5px 8px 4px',
                fontFamily: 'inherit',
            }}>
                {items.map((item, idx) => (
                    <React.Fragment key={item.name}>
                        {/* Item */}
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '2px 10px',
                        }}>
                            {/* Swatch */}
                            <span style={{
                                width: isLineOrArea ? 10 : 6,
                                height: isLineOrArea ? 2.5 : 6,
                                borderRadius: 2,
                                backgroundColor: item.color,
                                display: 'inline-block',
                                flexShrink: 0,
                                opacity: 0.9,
                            }} />
                            {/* Name */}
                            <span style={{
                                fontSize: 10.5,
                                fontWeight: 500,
                                color: isDark ? '#94a3b8' : '#64748b',
                                letterSpacing: '0.01em',
                                whiteSpace: 'nowrap',
                            }}>
                                {item.name}
                            </span>
                            {/* Value */}
                            {item.total !== null && (
                                <span style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: item.color,
                                    fontVariantNumeric: 'tabular-nums',
                                    letterSpacing: '-0.02em',
                                }}>
                                    {fmtTotal(item.total)}
                                </span>
                            )}
                        </div>
                        {/* Separator dot */}
                        {idx < items.length - 1 && (
                            <span style={{
                                width: 3,
                                height: 3,
                                borderRadius: '50%',
                                backgroundColor: sepColor,
                                display: 'inline-block',
                                flexShrink: 0,
                            }} />
                        )}
                    </React.Fragment>
                ))}
            </div>
        );
    }

    // ── Top / right legend (compact) ──────────────────────────────────────────
    const isRow = pos !== 'right';
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: isRow ? 'row' : 'column',
                flexWrap: 'wrap',
                gap: isRow ? '6px 12px' : '5px',
                justifyContent: isRow ? 'center' : 'flex-start',
                padding: pos === 'right' ? '4px 0 4px 10px' : '4px 0 0',
                fontSize: 11,
                color: labelColor,
                fontFamily: 'inherit',
            }}
        >
            {items.map((item) => (
                <span
                    key={item.name}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                >
                    <span
                        style={{
                            display: 'inline-block',
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: item.color,
                            flexShrink: 0,
                        }}
                    />
                    <span
                        style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: pos === 'right' ? 88 : 120,
                        }}
                    >
                        {item.name}
                    </span>
                </span>
            ))}
        </div>
    );
}
